import React, { useContext, useEffect, useMemo, useState } from "react";
import { UrlContext } from "../../../contexts/UrlContext";

export default function ScreeningQuestionsAnswer({ expert_id, project_id, profile }) {
  const { API_URL } = useContext(UrlContext);

  const [screeningAnswer, setScreeningAnswer] = useState([]);
  const [selectedChoices, setSelectedChoices] = useState({});

  const getScreeningAnswers = async () => {
    const requestBody = {
      projID: project_id,
      expertID: expert_id,
    };

    try {
      const response = await fetch(`${API_URL}fetch-question-answers`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const responseData = await response.json();

      if (responseData.status === "success") {
        const questions = responseData.screenQuestions || [];

        const updatedChoices = questions.reduce((acc, row, rowIndex) => {
          if (row.answerType === "Matrix" && row.answer) {
            acc[rowIndex] = row.answer;
          }
          return acc;
        }, {});

        setSelectedChoices(updatedChoices);
        setScreeningAnswer(questions);
      } else {
        console.error("Failed to fetch screening answers");
      }
    } catch (error) {
      console.error("Error while calling the backend API:", error);
    }
  };

  useEffect(() => {
    if (!expert_id || !project_id) return;
    getScreeningAnswers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expert_id, project_id]);

  const hasAnswers = useMemo(() => {
    return screeningAnswer?.some((row) => row?.answer);
  }, [screeningAnswer]);

  const parseYesNoAnswer = (answer) => {
    if (!answer) return { value: "", note: "" };

    if (answer.includes(";")) {
      const [value, note] = answer.split(";");
      return {
        value: value?.trim() || "",
        note: note?.trim() || "",
      };
    }

    return {
      value: answer.trim(),
      note: "",
    };
  };

  const isCheckedOption = (answer, option) => {
    if (!answer) return false;
    return answer.includes(option);
  };

  return (
    <div hidden={!profile.hasScreening} className="bg-white rounded-xl p-6 relative transition-all duration-300 shadow print-card-outer print-no-shadow print-border">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold print-text-lg">
          Screening Questions
        </h2>
      </div>

      {!hasAnswers ? (
        <p className="text-sm text-gray-500 print-text-sm">
          No screening data available.
        </p>
      ) : (
        <div className="space-y-6">
          {screeningAnswer.map((row, index) => {
            const yesNo = row.answerType === "Yes/No" ? parseYesNoAnswer(row.answer || "") : null;

            return (
              <div
                key={`${row.question}-${index}`}
                className="rounded-xl border border-gray-200 p-4 bg-gray-50/40 print-card-outer"
              >
                <h3 className="text-sm sm:text-base font-semibold text-[#365561] mb-3 print-text-md">
                  {index + 1}. {row.question}
                </h3>

                {row.answerType === "Open-Ended" ? (
                  <div className="rounded-lg bg-white border border-gray-200 px-4 py-3 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed print-text-sm">
                    {row.answer || "No answer provided"}
                  </div>
                ) : row.answerType === "Yes/No" ? (
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-4">
                      <div
                        className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm border ${
                          yesNo?.value === "Yes"
                            ? "border-[#365561] text-[#365561] bg-[#365561]/5"
                            : "border-gray-300 text-gray-500 bg-white"
                        }`}
                      >
                        <span
                          className={`h-2.5 w-2.5 rounded-full ${
                            yesNo?.value === "Yes" ? "bg-[#365561]" : "bg-gray-300"
                          }`}
                        />
                        Yes
                      </div>

                      <div
                        className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm border ${
                          yesNo?.value === "No"
                            ? "border-[#365561] text-[#365561] bg-[#365561]/5"
                            : "border-gray-300 text-gray-500 bg-white"
                        }`}
                      >
                        <span
                          className={`h-2.5 w-2.5 rounded-full ${
                            yesNo?.value === "No" ? "bg-[#365561]" : "bg-gray-300"
                          }`}
                        />
                        No
                      </div>
                    </div>

                    {yesNo?.note && (
                      <div className="rounded-lg bg-white border border-gray-200 px-4 py-3 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed print-text-sm">
                        {yesNo.note}
                      </div>
                    )}
                  </div>
                ) : row.answerType === "Confidence Level" ? (
                  <div className="flex flex-wrap gap-2">
                    {["High", "Medium", "Low", "Not allowed to discuss"].map((item) => {
                      const selected = row.answer === item;

                      return (
                        <div
                          key={item}
                          className={`rounded-full px-3 py-1 text-xs sm:text-sm border ${
                            selected
                              ? "border-[#365561] text-[#365561] bg-[#365561]/5 font-medium"
                              : "border-gray-300 text-gray-400 bg-white"
                          }`}
                        >
                          {item}
                        </div>
                      );
                    })}
                  </div>
                ) : row.answerType === "Matrix" ? (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[640px] border-collapse text-sm print-text-sm">
                      <thead>
                        <tr>
                          <th className="border border-gray-200 bg-gray-100 px-3 py-2 text-left font-medium text-[#365561]">
                            Option
                          </th>
                          {(row.choices || []).map((choice, choiceIndex) => (
                            <th
                              key={choiceIndex}
                              className="border border-gray-200 bg-gray-100 px-3 py-2 text-center font-medium text-[#365561]"
                            >
                              {choice || `Choice ${choiceIndex + 1}`}
                            </th>
                          ))}
                          <th className="border border-gray-200 bg-gray-100 px-3 py-2 text-left font-medium text-[#365561]">
                            Reason
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {(row.options || []).map((option, optionIndex) => (
                          <tr key={optionIndex}>
                            <td className="border border-gray-200 px-3 py-2 text-gray-700 bg-white">
                              {option || `Option ${optionIndex + 1}`}
                            </td>

                            {(row.choices || []).map((_, choiceIndex) => {
                              const checked =
                                selectedChoices[index]?.[optionIndex]?.choice === choiceIndex;

                              return (
                                <td
                                  key={choiceIndex}
                                  className="border border-gray-200 px-3 py-2 text-center bg-white"
                                >
                                  <span
                                    className={`inline-block h-4 w-4 rounded-full border ${
                                      checked
                                        ? "border-[#365561] bg-[#365561]"
                                        : "border-gray-300 bg-white"
                                    }`}
                                  />
                                </td>
                              );
                            })}

                            <td className="border border-gray-200 px-3 py-2 text-gray-700 bg-white whitespace-pre-wrap">
                              {selectedChoices[index]?.[optionIndex]?.reason || ""}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {(row.options || []).map((option, optIndex) => {
                      const checked = isCheckedOption(row.answer || "", option);

                      return (
                        <div
                          key={optIndex}
                          className="flex items-center gap-3 text-sm text-gray-700 print-text-sm"
                        >
                          <span
                            className={`inline-block h-4 w-4 rounded-sm border ${
                              checked
                                ? "border-[#365561] bg-[#365561]"
                                : "border-gray-300 bg-white"
                            }`}
                          />
                          <span>{option}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}